import { Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormLabel, IconButton, InputLabel, LinearProgress, MenuItem, Radio, RadioGroup, Select, TextField, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Environment } from '../../../../environment';
import { dateFormat } from '../../../../helpers/dateFormat';
import { LayoutRegister } from '../../../../layouts';
import {
  IProductView, IServiceView, IUpdateAppointment, ICompleteAppointment,
  appointmentService, businessService, clientPackageService, IClientPackageView, feedback, finishRequest, productService, serviceService, caixaService,
  businessEmployeeService, IBusinessEmployeeView,
} from '../../../../services';
import { Api } from '../../../../services/api/axios-config';
import { setCsrfRequestToken } from '../../../../services/api/axios-config/interceptors';

interface IProps {
  appointmentId: number;
  refetch: () => void;
  onCloseEdit: () => void;
}

interface IPaymentSplit {
  paymentMethod: number;
  amount: number;
}

export const AppointmentFinancePage: React.FC<IProps> = ({ appointmentId, refetch, onCloseEdit }) => {
  const queryClient = useQueryClient();
  const [selectedServices, setSelectedServices] = useState<IServiceView[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<IProductView[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number | null>(null);
  const [useSplitPayment, setUseSplitPayment] = useState(false);
  const [paymentSplits, setPaymentSplits] = useState<IPaymentSplit[]>([{ paymentMethod: 0, amount: 0 }]);
  const [addToCaixa, setAddToCaixa] = useState(true);
  const [packageUsedServiceIds, setPackageUsedServiceIds] = useState<number[]>([]);
  const [showCaixaWarning, setShowCaixaWarning] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [itemSellers, setItemSellers] = useState<Record<string, number>>({});
  const [showRenewalDialog, setShowRenewalDialog] = useState(false);
  const [renewalTarget, setRenewalTarget] = useState<IClientPackageView | null>(null);
  const [renewalSellerId, setRenewalSellerId] = useState<number | undefined>(undefined);

  const paymentMethods = [
    { value: 0, label: 'Dinheiro' },
    { value: 1, label: 'PIX' },
    { value: 2, label: 'Débito' },
    { value: 3, label: 'Crédito' },
    { value: 4, label: 'Outro' },
  ];

  const { data: appointment, isFetching: isFetchingAppointment } = useQuery({
    queryKey: ['appointment', appointmentId],
    queryFn: () => appointmentService.getById(appointmentId),
    enabled: !!appointmentId
  });

  const { data: serviceDetails, isFetching: isFetchingServicesAppointment } = useQuery({
    queryKey: ['service', appointment?.services],
    queryFn: () => serviceService.getByIds(appointment?.services?.map(x => x.id) || []),
    enabled: !!appointment?.services && appointment.services.length > 0,
  });

  useEffect(() => {
    if (serviceDetails) setSelectedServices(serviceDetails);
  }, [serviceDetails]);

  const { data: productDetails, isFetching: isFetchingProductsAppointment } = useQuery({
    queryKey: ['product', appointment?.products],
    queryFn: () => productService.getByIds(appointment?.products?.map(x => x.id) || []),
    enabled: !!appointment?.products && appointment.products.length > 0,
  });

  useEffect(() => {
    if (productDetails) setSelectedProducts(productDetails);
  }, [productDetails]);

  const { data: services, isFetching: isFetchingServices } = useQuery({
    queryKey: ['service', appointment?.businessId],
    queryFn: () => serviceService.getByBusiness(appointment?.businessId || 0, true),
    enabled: !!appointment?.businessId
  });

  const { data: products, isFetching: isFetchingProducts } = useQuery({
    queryKey: ['product', appointment?.businessId],
    queryFn: () => productService.getByBusiness(appointment?.businessId || 0, true),
    enabled: !!appointment?.businessId
  });

  const { data: businessEmployees } = useQuery({
    queryKey: ['businessEmployees', appointment?.businessId],
    queryFn: () => businessEmployeeService.get(appointment!.businessId, true),
    enabled: !!appointment?.businessId,
  });

  const { data: currentCaixa } = useQuery({
    queryKey: ['caixaCurrent', appointment?.businessId],
    queryFn: () => caixaService.getCurrent(appointment?.businessId || 0),
    enabled: !!appointment?.businessId
  });

  useQuery({
    queryKey: ['clientPackages', appointment?.userId, appointment?.businessId],
    queryFn: () => clientPackageService.getByClient(appointment!.userId!, appointment!.businessId),
    enabled: !!appointment?.userId && !!appointment?.businessId && !appointment?.completed,
  });

  const { data: allClientPackages } = useQuery({
    queryKey: ['clientPackagesAll', appointment?.userId, appointment?.businessId],
    queryFn: () => clientPackageService.getByClientAll(appointment!.userId!, appointment!.businessId),
    enabled: !!appointment?.userId && !!appointment?.businessId && !appointment?.completed,
  });

  const pendingPackages = useMemo(() => {
    if (!allClientPackages) return [];
    const pending = allClientPackages.filter(cp => cp.status === 'Pending');
    // Deduplicate by serviceId: keep only the most recent per service
    const map = new Map<number, IClientPackageView>();
    for (const pkg of pending) {
      const existing = map.get(pkg.serviceId);
      if (!existing || new Date(pkg.purchasedAt).getTime() > new Date(existing.purchasedAt).getTime()) {
        map.set(pkg.serviceId, pkg);
      }
    }
    return Array.from(map.values());
  }, [allClientPackages]);

  const pendingPackageForService = useMemo(() => {
    if (!pendingPackages.length) return new Map<number, IClientPackageView>();
    const map = new Map<number, IClientPackageView>();
    for (const pkg of pendingPackages) {
      map.set(pkg.serviceId, pkg);
    }
    return map;
  }, [pendingPackages]);

  const { data: business } = useQuery({
    queryKey: ['business', appointment?.businessId],
    queryFn: () => businessService.getById(appointment!.businessId),
    enabled: !!appointment?.businessId,
  });

  const availablePaymentMethods = useMemo(() => {
    if (!business?.paymentMethods) return paymentMethods;
    const accepted = new Set(business.paymentMethods.split(',').map(s => s.trim()));
    return paymentMethods.filter(m => accepted.has(m.label));
  }, [business?.paymentMethods]);

  // Se o business exige caixa aberto, sempre adiciona ao caixa (sem checkbox)
  const caixaRequired = business?.requireOpenCaixa === true;
  useEffect(() => {
    if (caixaRequired) setAddToCaixa(true);
  }, [caixaRequired]);

  const { mutate: cancelAppointment, isLoading: isLoadingCancel } = useMutation(
    async (body: IUpdateAppointment) => {
      try {
        const tokenRes = await Api.get('/auth/antiforgery-token');
        if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      } catch {
        // best-effort
      }
      return appointmentService.update(body);
    },
    {
      onSuccess: () => {
        finishRequest('Agendamento cancelado', 'success', () => { refetch(); onCloseEdit(); });
      },
      onError: (error: Error) => finishRequest(String(error), 'error'),
    }
  );

  const { mutate: finishAppointment, isLoading: isLoadingFinishAppointment } = useMutation(
    async (body: ICompleteAppointment) => {
      try {
        const tokenRes = await Api.get('/auth/antiforgery-token');
        if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
      } catch {
        // best-effort
      }
      return appointmentService.complete(body.id, body);
    },
    {
      onSuccess: () => {
        if (addToCaixa && appointment?.businessId) {
          queryClient.invalidateQueries({ queryKey: ['caixaCurrent', appointment.businessId] });
          queryClient.invalidateQueries({ queryKey: ['caixaHistory', appointment.businessId] });
        }
        finishRequest('Agendamento finalizado', 'success', () => { refetch(); onCloseEdit(); });
      },
      onError: (error: Error) => finishRequest(String(error), 'error'),
    }
  );

  const { mutate: renewPackage } = useMutation(
    (params: { id: number; paymentMethod?: string; sellerBusinessEmployeeId?: number }) =>
      clientPackageService.renew(params.id, { paymentMethod: params.paymentMethod, sellerBusinessEmployeeId: params.sellerBusinessEmployeeId }),
    {
      onSuccess: () => {
        feedback('Pacote renovado com sucesso!', 'success');
        queryClient.invalidateQueries({ queryKey: ['clientPackages', appointment?.userId, appointment?.businessId] });
        queryClient.invalidateQueries({ queryKey: ['clientPackagesAll', appointment?.userId, appointment?.businessId] });
      },
    }
  );

  const servicesTotalAmount = useMemo(() => selectedServices.reduce((t, s) => t + s.amount, 0), [selectedServices]);
  const productsTotalAmount = useMemo(() => selectedProducts.reduce((t, p) => t + p.amount, 0), [selectedProducts]);
  const totalAmount = servicesTotalAmount + productsTotalAmount;

  // --- Package logic ---
  const packageCoveredServiceIds = useMemo(() => {
    if (!allClientPackages || !selectedServices.length) return new Set<number>();
    // Deduplicate by serviceId: prefer active package, else most recent pending
    const sorted = [...allClientPackages].sort((a, b) => {
      if (a.status === 'Active' && b.status !== 'Active') return -1;
      if (a.status !== 'Active' && b.status === 'Active') return 1;
      return new Date(b.purchasedAt).getTime() - new Date(a.purchasedAt).getTime();
    });
    return new Set(sorted.map(pkg => pkg.serviceId));
  }, [allClientPackages, selectedServices]);

  const packageDiscount = useMemo(() => {
    if (appointment?.completed) return appointment.packageDiscount ?? 0;
    return selectedServices
      .filter(s => packageUsedServiceIds.includes(s.id))
      .reduce((t, s) => t + s.amount, 0);
  }, [selectedServices, packageUsedServiceIds, appointment?.completed, appointment?.packageDiscount]);

  const finalAmount = totalAmount - packageDiscount;
  // ---

  const splitTotal = useMemo(() => paymentSplits.reduce((t, s) => t + s.amount, 0), [paymentSplits]);
  const remainingAmount = finalAmount - splitTotal;

  useEffect(() => {
    const validIds = new Set(selectedServices.map(s => s.id));
    setPackageUsedServiceIds(prev => prev.filter(id => validIds.has(id)));
  }, [selectedServices]);

  // Initialize payment fields when appointment loads
  useEffect(() => {
    if (!appointment) return;
    if (appointment.paymentSplits && appointment.paymentSplits.length > 0) {
      setUseSplitPayment(true);
      setPaymentSplits(appointment.paymentSplits.map(s => ({ paymentMethod: s.paymentMethod, amount: s.amount })));
      setSelectedPaymentMethod(null);
    } else if (appointment.paymentMethod !== null && appointment.paymentMethod !== undefined) {
      setSelectedPaymentMethod(appointment.paymentMethod);
      setUseSplitPayment(false);
    }
  }, [appointment]);

  const handleSplitChange = (index: number, field: keyof IPaymentSplit, value: number) => {
    const newSplits = [...paymentSplits];
    // Clamp amount to the remaining total (never allow more than what's left)
    if (field === 'amount') {
      const otherSplitsTotal = paymentSplits.reduce((t, s, i) => i !== index ? t + s.amount : t, 0);
      const maxAllowed = Math.max(0, finalAmount - otherSplitsTotal);
      value = Math.min(Math.max(0, value || 0), maxAllowed);
    }
    newSplits[index] = { ...newSplits[index], [field]: value };
    setPaymentSplits(newSplits);
  };

  const addSplit = () => {
    const defaultMethod = availablePaymentMethods.length > 0 ? availablePaymentMethods[0].value : 0;
    setPaymentSplits([...paymentSplits, { paymentMethod: defaultMethod, amount: 0 }]);
  };

  const removeSplit = (index: number) => {
    if (paymentSplits.length > 1) {
      setPaymentSplits(paymentSplits.filter((_, i) => i !== index));
    }
  };

  const doSubmit = useCallback(() => {
    if (!appointment) return;

    const consumedServices = packageUsedServiceIds.length > 0 ? packageUsedServiceIds : undefined;
    const baseBody: ICompleteAppointment = {
      id: appointment.id,
      services: selectedServices.map(x => ({ 
        serviceId: x.id, 
        soldByBusinessEmployeeId: itemSellers[`service-${x.id}`] ?? appointment.businessEmployeeId 
      })),
      products: selectedProducts.map(x => ({ 
        productId: x.id, 
        soldByBusinessEmployeeId: itemSellers[`product-${x.id}`] ?? appointment.businessEmployeeId 
      })),
      packageConsumedServiceIds: consumedServices,
    };

    if (useSplitPayment) {
      const validSplits = paymentSplits.filter(s => s.amount > 0);
      if (validSplits.length === 0) return;
      finishAppointment({ ...baseBody, paymentSplits: paymentSplits.filter(s => s.amount > 0) });
    } else {
      finishAppointment({ ...baseBody, paymentMethod: selectedPaymentMethod ?? undefined });
    }
  }, [appointment, selectedServices, selectedProducts, selectedPaymentMethod, useSplitPayment, paymentSplits, packageUsedServiceIds, itemSellers]);

  const handleSubmit = useCallback(() => {
    if (!appointment) return;

    // If any selected service uses a pending package, show renewal dialog
    if (packageUsedServiceIds.length > 0) {
      const pendingServiceId = packageUsedServiceIds.find(sid => pendingPackageForService.has(sid));
      if (pendingServiceId !== undefined) {
        const pkg = pendingPackageForService.get(pendingServiceId);
        if (pkg) {
          setRenewalTarget(pkg);
          setRenewalSellerId(undefined);
          setShowRenewalDialog(true);
          return;
        }
      }
    }

    // If addToCaixa is checked but there's no open caixa, show confirmation modal
    if (addToCaixa && (!currentCaixa || !currentCaixa.isOpen)) {
      setShowCaixaWarning(true);
      return;
    }

    doSubmit();
  }, [appointment, addToCaixa, currentCaixa, doSubmit, packageUsedServiceIds, pendingPackageForService]);

  const handleCancel = useCallback(async () => {
    if (!appointment) return;
    cancelAppointment({
      id: appointment.id,
      canceled: true,
      date: appointment.date,
      businessId: appointment.businessId,
      businessEmployeeId: appointment.businessEmployeeId,
      services: appointment.services.map(x => ({ serviceId: x.id })),
      products: appointment.products.map(x => ({ productId: x.id })),
      userId: appointment.userId,
    });
  }, [appointment, cancelAppointment]);

  const isFetching = isFetchingAppointment || isFetchingServicesAppointment ||
    isFetchingProductsAppointment || isFetchingServices || isFetchingProducts;

  return (
    <Box>
      <LayoutRegister
        header={appointment?.completed ? 'Agendamento finalizado' : 'Finalizar agendamento'}
        onClickSave={handleSubmit}
        isLoading={isLoadingFinishAppointment}
        disabled={appointment?.completed || (finalAmount > 0 && selectedPaymentMethod === null && !useSplitPayment)}
        saveButtonDataTestId='btn-finish-appointment'
      >
        {isFetching && <LinearProgress />}

        {appointment && services && products && !isFetching && (
          <Box display='flex' flexDirection='column' gap={2}>

            {/* Receipt header */}
            <Box
              display='flex'
              flexDirection='column'
              gap={0.5}
              px={2}
              py={1.5}
              sx={{ borderRadius: '10px', backgroundColor: 'action.hover' }}
            >
              <Box display='flex' justifyContent='space-between'>
                <Typography variant='caption' color='text.secondary'>Cliente</Typography>
                <Typography variant='body2' fontWeight={600}>{appointment.customerName}</Typography>
              </Box>
              <Box display='flex' justifyContent='space-between'>
                <Typography variant='caption' color='text.secondary'>Profissional</Typography>
                <Typography variant='body2'>{appointment.employeeName}</Typography>
              </Box>
              <Box display='flex' justifyContent='space-between'>
                <Typography variant='caption' color='text.secondary'>Data</Typography>
                <Typography variant='body2'>{dateFormat(appointment.date, 'LLL')}</Typography>
              </Box>
              <Box display='flex' justifyContent='space-between'>
                <Typography variant='caption' color='text.secondary'>Comanda #</Typography>
                <Typography variant='body2' fontWeight={600}>{appointment.appointmentNumber}</Typography>
              </Box>
            </Box>

            <Divider />

            {/* Services */}
            <Box>
              <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                <Typography variant='subtitle2' fontWeight={700}>Serviços</Typography>
                <Typography variant='caption' color='text.secondary'>
                  Subtotal: R$ {servicesTotalAmount.toFixed(2)}
                </Typography>
              </Box>
              <Autocomplete
                multiple
                disabled={appointment.completed}
                options={services.filter(x => !selectedServices.some(y => x.id === y.id))}
                getOptionLabel={opt => `${opt.description} — R$ ${opt.amount.toFixed(2)}`}
                onChange={(_, value) => setSelectedServices(value)}
                value={selectedServices}
                noOptionsText={Environment.emptyListInput}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => {
                    const { key, ...tagProps } = getTagProps({ index: idx });
                    return (
                      <Chip
                        key={key}
                        label={`${opt.description} — R$ ${opt.amount.toFixed(2)}`}
                        size='small'
                        {...tagProps}
                      />
                    );
                  })
                }
                renderInput={params => (
                  <TextField {...params} label='Adicionar serviço' variant='outlined' size='small' />
                )}
              />
            </Box>

            {/* Package usage — only for non-completed appointments with client packages */}
            {!appointment?.completed && packageCoveredServiceIds.size > 0 && selectedServices.some(s => packageCoveredServiceIds.has(s.id)) && (
              <Box>
                <Typography variant='subtitle2' fontWeight={700} mb={1}>Pacotes disponíveis</Typography>
                {selectedServices.map(svc => {
                  if (!packageCoveredServiceIds.has(svc.id)) return null;
                  const isUsed = packageUsedServiceIds.includes(svc.id);
                  const isPending = pendingPackageForService.has(svc.id);
                  return (
                    <FormControlLabel
                      key={svc.id}
                      control={
                        <Checkbox
                          size='small'
                          checked={isUsed}
                          onChange={(_, checked) => {
                            setPackageUsedServiceIds(prev =>
                              checked
                                ? prev.includes(svc.id) ? prev : [...prev, svc.id]
                                : prev.filter(id => id !== svc.id)
                            );
                          }}
                        />
                      }
                      label={
                        <Typography variant='body2'>
                          Utilizar pacote para <strong>{svc.description}</strong>
                          {isPending && (
                            <Chip label='Pendente' color='warning' size='small' sx={{ ml: 1 }} />
                          )}
                          {isUsed
                            ? <Typography component='span' variant='body2' color='success.main'> — R$ 0,00</Typography>
                            : <Typography component='span' variant='body2' color='text.secondary'> — R$ {svc.amount.toFixed(2)}</Typography>
                          }
                        </Typography>
                      }
                    />
                  );
                })}
              </Box>
            )}

            {/* Pending package alert */}
            {!appointment?.completed && pendingPackages.length > 0 && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: 'warning.light',
                  border: '1px solid',
                  borderColor: 'warning.main',
                }}
              >
                <Typography variant='body2' fontWeight={600} color='warning.dark'>
                  Pacote com renovação pendente
                </Typography>
                {pendingPackages.map(cp => (
                  <Typography key={cp.id} variant='caption' display='block' color='warning.dark'>
                    {cp.packageName} — {cp.userName} (ciclo {cp.cycle}º, {cp.remainingUses}/{cp.totalUses} usos)
                  </Typography>
                ))}
              </Box>
            )}

            {/* Products */}

            <Box>
              <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                <Typography variant='subtitle2' fontWeight={700}>Produtos</Typography>
                <Typography variant='caption' color='text.secondary'>
                  Subtotal: R$ {productsTotalAmount.toFixed(2)}
                </Typography>
              </Box>
              <Autocomplete
                multiple
                disabled={appointment.completed}
                options={products.filter(x => !selectedProducts.some(y => x.id === y.id))}
                getOptionLabel={opt => `${opt.description} — R$ ${opt.amount.toFixed(2)}`}
                onChange={(_, value) => setSelectedProducts(value)}
                value={selectedProducts}
                noOptionsText={Environment.emptyListInput}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => {
                    const { key, ...tagProps } = getTagProps({ index: idx });
                    return (
                      <Chip
                        key={key}
                        label={`${opt.description} — R$ ${opt.amount.toFixed(2)}`}
                        size='small'
                        color='secondary'
                        {...tagProps}
                      />
                    );
                  })
                }
                renderInput={params => (
                  <TextField {...params} label='Adicionar produto' variant='outlined' size='small' />
                )}
              />
            </Box>

            <Divider />

            {/* Total */}
            <Box
              display='flex'
              flexDirection='column'
              px={2}
              py={1.5}
              sx={{ borderRadius: '10px', backgroundColor: 'primary.main', color: '#fff' }}
            >
              {packageDiscount > 0 && (
                <Box display='flex' justifyContent='space-between' mb={0.5}>
                  <Typography variant='body2' color='inherit'>Subtotal</Typography>
                  <Typography variant='body2' color='inherit' sx={{ textDecoration: 'line-through', opacity: 0.7 }}>
                    R$ {totalAmount.toFixed(2)}
                  </Typography>
                </Box>
              )}
              {packageDiscount > 0 && (
                <Box display='flex' justifyContent='space-between' mb={0.5}>
                  <Typography variant='body2' color='inherit'>Desconto pacotes</Typography>
                  <Typography variant='body2' color='inherit'>- R$ {packageDiscount.toFixed(2)}</Typography>
                </Box>
              )}
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography variant='subtitle1' fontWeight={600} color='inherit'>Total</Typography>
                <Typography variant='h6' fontWeight={700} color='inherit'>
                  R$ {finalAmount.toFixed(2)}
                </Typography>
              </Box>
            </Box>

            {/* Payment Method — read-only summary for completed appointments */}
            {appointment?.completed && useSplitPayment && appointment.paymentSplits && (
              <Box px={2} py={1.5} sx={{ borderRadius: '10px', backgroundColor: 'action.hover' }}>
                <Typography variant='subtitle2' fontWeight={600} mb={1}>Pagamento</Typography>
                {appointment.paymentSplits.map((split, index) => (
                  <Box key={index} display='flex' justifyContent='space-between' alignItems='center'>
                    <Typography variant='body2'>{split.paymentMethodName}</Typography>
                    <Typography variant='body2' fontWeight={600}>R$ {split.amount.toFixed(2)}</Typography>
                  </Box>
                ))}
              </Box>
            )}
            {appointment?.completed && !useSplitPayment && selectedPaymentMethod !== null && (
              <Box px={2} py={1.5} sx={{ borderRadius: '10px', backgroundColor: 'action.hover' }}>
                <Typography variant='subtitle2' fontWeight={600} mb={0.5}>Pagamento</Typography>
                <Typography variant='body2'>
                  {availablePaymentMethods.find(m => m.value === selectedPaymentMethod)?.label}
                </Typography>
              </Box>
            )}

            {/* Dividir Comissões button */}
            {!appointment?.completed && (
              <Box>
                <Button
                  variant='outlined'
                  size='small'
                  onClick={() => setSplitOpen(true)}
                  fullWidth
                >
                  Dividir Comissões
                </Button>
                {Object.keys(itemSellers).length > 0 && (
                  <Typography variant='caption' color='success.main' display='block' sx={{ mt: 0.5 }}>
                    {Object.keys(itemSellers).length} item(ns) com comissão dividida
                  </Typography>
                )}
              </Box>
            )}

            {/* Payment Method — editable for non-completed */}
            {!appointment?.completed && finalAmount > 0 && (
              <FormControl component='fieldset' required>
                <FormLabel component='legend'>Forma de Pagamento</FormLabel>
                <RadioGroup
                  row
                  value={selectedPaymentMethod ?? ''}
                  onChange={(e) => setSelectedPaymentMethod(Number(e.target.value))}
                >
                  {availablePaymentMethods.map((method) => (
                    <FormControlLabel
                      key={method.value}
                      value={method.value}
                      control={<Radio />}
                      label={method.label}
                      disabled={useSplitPayment}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}

            {/* Split Payment Option — editable for non-completed */}
            {!appointment?.completed && finalAmount > 0 && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useSplitPayment}
                      onChange={(e) => {
                        setUseSplitPayment(e.target.checked);
                        if (e.target.checked) setSelectedPaymentMethod(null);
                      }}
                    />
                  }
                  label="Pagamento dividido"
                />
                
                {useSplitPayment && (
                  <Box pl={4} display='flex' flexDirection='column' gap={1}>
                    {paymentSplits.map((split, index) => (
                      <Box key={index} display='flex' gap={1} alignItems='center'>
                        <FormControl size='small'>
                          <Select
                            value={split.paymentMethod}
                            onChange={(e) => handleSplitChange(index, 'paymentMethod', Number(e.target.value))}
                            size='small'
                            sx={{ minWidth: 120 }}
                          >
                            {availablePaymentMethods.map((method) => (
                              <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label='Valor'
                          value={split.amount > 0 ? split.amount.toFixed(2) : ''}
                          placeholder='0,00'
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            const value = raw ? parseFloat(raw) / 100 : 0;
                            handleSplitChange(index, 'amount', value);
                          }}
                          size='small'
                          sx={{ width: 110 }}
                          InputProps={{
                            startAdornment: <Typography variant='body2' sx={{ mr: 0.5, color: 'text.secondary' }}>R$</Typography>,
                          }}
                          inputProps={{
                            inputMode: 'decimal',
                          }}
                        />
                        <TextField
                          label='Restante'
                          value={remainingAmount > 0 ? remainingAmount.toFixed(2) : '0.00'}
                          disabled
                          size='small'
                          sx={{ width: 100 }}
                        />
                        <IconButton size='small' onClick={() => removeSplit(index)} disabled={paymentSplits.length <= 1}>
                          <Chip label="✕" size='small' />
                        </IconButton>
                      </Box>
                    ))}
                    <Button size='small' onClick={addSplit}>
                      + Adicionar Split
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Add to Caixa */}
            {!appointment.completed && finalAmount > 0 && !caixaRequired && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addToCaixa}
                      onChange={(e) => setAddToCaixa(e.target.checked)}
                    />
                  }
                  label="Adicionar ao caixa"
                />
                {addToCaixa && currentCaixa && !currentCaixa.isOpen && (
                  <Typography variant='caption' color='warning.main' display='block'>
                    Aviso: O caixa está fechado. A receita não será adicionada ao caixa atual.
                  </Typography>
                )}
                {addToCaixa && !currentCaixa && (
                  <Typography variant='caption' color='warning.main' display='block'>
                    Aviso: Não há caixa aberto. A receita não será adicionada.
                  </Typography>
                )}
              </Box>
            )}

            {/* Cancel Button */}
            {!appointment.completed && (
              <Button
                variant='outlined'
                color='error'
                onClick={handleCancel}
                disabled={isLoadingCancel || isLoadingFinishAppointment}
                fullWidth
              >
                Cliente não compareceu
              </Button>
            )}
          </Box>
        )}

        {/* Caixa warning confirmation modal */}
        <Dialog open={showCaixaWarning} onClose={() => setShowCaixaWarning(false)} maxWidth='xs'>
          <DialogTitle>Caixa não está aberto</DialogTitle>
          <DialogContent>
            <Typography>
              Não há um caixa aberto. O valor não será adicionado ao caixa.
            </Typography>
            <Typography mt={1}>
              Você tem certeza que deseja finalizar o agendamento mesmo assim?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCaixaWarning(false)} color='inherit'>
              Não, voltar
            </Button>
            <Button
              onClick={() => {
                setShowCaixaWarning(false);
                doSubmit();
              }}
              variant='contained'
              color='primary'
            >
              Sim, salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dividir Comissões modal */}
        <Dialog open={splitOpen} onClose={() => setSplitOpen(false)} maxWidth='md' fullWidth>
          <DialogTitle>Dividir Comissões</DialogTitle>
          <DialogContent>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Selecione para quem vai a comissão de cada item. Por padrão, tudo vai para o profissional da comanda.
            </Typography>
            <TableContainer component={Paper} variant='outlined'>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell>Comissão para</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedServices.map((svc) => (
                    <TableRow key={`service-${svc.id}`}>
                      <TableCell>{svc.description}</TableCell>
                      <TableCell>Serviço</TableCell>
                      <TableCell>R$ {svc.amount.toFixed(2)}</TableCell>
                      <TableCell>
                        <Select
                          size='small'
                          value={itemSellers[`service-${svc.id}`] ?? ''}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            setItemSellers(prev => {
                              if (!val) {
                                const next = { ...prev };
                                delete next[`service-${svc.id}`];
                                return next;
                              }
                              return { ...prev, [`service-${svc.id}`]: val };
                            });
                          }}
                          displayEmpty
                          sx={{ minWidth: 150 }}
                        >
                          <MenuItem value=''>
                            <em>{appointment?.employeeName} (padrão)</em>
                          </MenuItem>
                          {businessEmployees?.filter((be: IBusinessEmployeeView) => be.id !== appointment?.businessEmployeeId).map((be: IBusinessEmployeeView) => (
                            <MenuItem key={be.id} value={be.id}>{be.name}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                  {selectedProducts.map((prod) => (
                    <TableRow key={`product-${prod.id}`}>
                      <TableCell>{prod.description}</TableCell>
                      <TableCell>Produto</TableCell>
                      <TableCell>R$ {prod.amount.toFixed(2)}</TableCell>
                      <TableCell>
                        <Select
                          size='small'
                          value={itemSellers[`product-${prod.id}`] ?? ''}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            setItemSellers(prev => {
                              if (!val) {
                                const next = { ...prev };
                                delete next[`product-${prod.id}`];
                                return next;
                              }
                              return { ...prev, [`product-${prod.id}`]: val };
                            });
                          }}
                          displayEmpty
                          sx={{ minWidth: 150 }}
                        >
                          <MenuItem value=''>
                            <em>{appointment?.employeeName} (padrão)</em>
                          </MenuItem>
                          {businessEmployees?.filter((be: IBusinessEmployeeView) => be.id !== appointment?.businessEmployeeId).map((be: IBusinessEmployeeView) => (
                            <MenuItem key={be.id} value={be.id}>{be.name}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSplitOpen(false)} color='inherit'>Fechar</Button>
            <Button onClick={() => setSplitOpen(false)} variant='contained'>Confirmar Divisão</Button>
          </DialogActions>
        </Dialog>

        {/* Renewal confirmation dialog */}
        <Dialog open={showRenewalDialog} onClose={() => setShowRenewalDialog(false)} maxWidth='xs'>
          <DialogTitle>Renovação de Pacote</DialogTitle>
          <DialogContent>
            {renewalTarget && (
              <Box display='flex' flexDirection='column' gap={1}>
                <Typography>
                  <strong>Cliente:</strong> {renewalTarget.userName}
                </Typography>
                <Typography>
                  <strong>Pacote:</strong> {renewalTarget.packageName}
                </Typography>
                <Typography>
                  <strong>Valor:</strong> R$ {renewalTarget.packageAmount.toFixed(2)}
                </Typography>
                <FormControl fullWidth size='small' sx={{ mt: 1 }}>
                  <InputLabel>Profissional (comissão)</InputLabel>
                  <Select
                    value={renewalSellerId ?? ''}
                    label='Profissional (comissão)'
                    onChange={(e) => setRenewalSellerId(e.target.value ? Number(e.target.value) : undefined)}
                  >
                    <MenuItem value=''>
                      <em>Selecionar</em>
                    </MenuItem>
                    {businessEmployees?.filter((be: IBusinessEmployeeView) => be.isActive).map((be: IBusinessEmployeeView) => (
                      <MenuItem key={be.id} value={be.id}>
                        {be.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant='body2' color='text.secondary' mt={1}>
                  O cliente pagou a renovação deste pacote?
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowRenewalDialog(false);
                setRenewalTarget(null);
                setRenewalSellerId(undefined);
                // Remove pending services from used list since they weren't renewed
                setPackageUsedServiceIds(prev =>
                  prev.filter(sid => !pendingPackageForService.has(sid))
                );
                if (addToCaixa && (!currentCaixa || !currentCaixa.isOpen)) {
                  setShowCaixaWarning(true);
                } else {
                  doSubmit();
                }
              }}
              color='inherit'
            >
              Não
            </Button>
            <Button
              onClick={() => {
                if (!renewalTarget) return;
                renewPackage(
                  { id: renewalTarget.id, sellerBusinessEmployeeId: renewalSellerId },
                  {
                    onSuccess: () => {
                      setShowRenewalDialog(false);
                      setRenewalTarget(null);
                      setRenewalSellerId(undefined);
                      if (addToCaixa && (!currentCaixa || !currentCaixa.isOpen)) {
                        setShowCaixaWarning(true);
                      } else {
                        doSubmit();
                      }
                    },
                  }
                );
              }}
              variant='contained'
              color='primary'
            >
              Sim, renovar
            </Button>
          </DialogActions>
        </Dialog>
      </LayoutRegister>

    </Box>
  );
};